import React, { useState, useEffect } from 'react';
import './DRPCDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaUserGraduate,
  FaCheckCircle,
  FaClock,
  FaChartBar,
  FaHospital,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaEye,
  FaAward,
  FaFileAlt,
  FaSignOutAlt,
  FaUserMd,
  FaCheck
} from 'react-icons/fa';

const DRPCDashboard = ({ onNavigate }) => {
  const [user, setUser] = useState(null);
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, attendance, reports, evaluate
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  // Statistics
  const [stats, setStats] = useState({
    totalStudents: 0,
    pendingAttendance: 0,
    pendingReports: 0,
    completedPostings: 0
  });

  // Get user data
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!token || !userData) {
      if (onNavigate) {
        onNavigate('login');
      }
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'drpc') {
        onNavigate('login');
        return;
      }
      setUser(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      if (onNavigate) {
        onNavigate('login');
      }
    }
  }, [onNavigate]);

  // Fetch postings in DRPC's district
  useEffect(() => {
    if (user) {
      fetchPostings();
    }
  }, [user]);

  const fetchPostings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${collegeConfig.api.baseURL}/drp/drpc/postings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPostings(data.postings || []);
        
        // Calculate statistics
        const totalStudents = data.postings.length;
        const pendingAttendance = data.postings.reduce((count, p) => {
          return count + (p.attendance?.filter(a => !a.verifiedByDRPC).length || 0);
        }, 0);
        const pendingReports = data.postings.reduce((count, p) => {
          return count + (p.weeklyReports?.filter(r => !r.verifiedByDRPC).length || 0);
        }, 0);
        const completedPostings = data.postings.filter(p => p.isCompleted).length;
        
        setStats({
          totalStudents,
          pendingAttendance,
          pendingReports,
          completedPostings
        });
      } else {
        setError('Failed to fetch postings');
      }
    } catch (error) {
      console.error('Error fetching postings:', error);
      setError('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAttendance = async (postingId, attendanceId, remarks = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/drpc/attendance/${postingId}/${attendanceId}/verify`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ remarks })
        }
      );
      
      if (response.ok) {
        alert('✅ Attendance verified successfully!');
        fetchPostings();
        if (selectedStudent) {
          // Refresh selected student data
          const posting = postings.find(p => p._id === selectedStudent._id);
          setSelectedStudent(posting);
        }
      } else {
        alert('❌ Failed to verify attendance');
      }
    } catch (error) {
      console.error('Error verifying attendance:', error);
      alert('❌ Error verifying attendance');
    }
  };

  const handleBulkVerifyAttendance = async (postingId, attendanceIds) => {
    if (!window.confirm(`Verify ${attendanceIds.length} attendance records?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/drpc/attendance/${postingId}/bulk-verify`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            attendanceIds,
            remarks: 'Bulk verified by DRPC'
          })
        }
      );
      
      if (response.ok) {
        alert('✅ Attendance records verified successfully!');
        fetchPostings();
        if (selectedStudent) {
          const posting = postings.find(p => p._id === selectedStudent._id);
          setSelectedStudent(posting);
        }
      } else {
        alert('❌ Failed to verify attendance');
      }
    } catch (error) {
      console.error('Error bulk verifying:', error);
      alert('❌ Error verifying attendance');
    }
  };

  const handleVerifyReport = async (postingId, reportId, feedback = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/drpc/weekly-report/${postingId}/${reportId}/verify`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ feedback })
        }
      );
      
      if (response.ok) {
        alert('✅ Weekly report verified successfully!');
        fetchPostings();
        if (selectedStudent) {
          const posting = postings.find(p => p._id === selectedStudent._id);
          setSelectedStudent(posting);
        }
      } else {
        alert('❌ Failed to verify report');
      }
    } catch (error) {
      console.error('Error verifying report:', error);
      alert('❌ Error verifying report');
    }
  };

  const handleEvaluate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/drpc/posting/${selectedStudent._id}/evaluate`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            drpcScore: parseInt(formData.get('drpcScore')),
            drpcRemarks: formData.get('drpcRemarks'),
            clinicalSkills: parseInt(formData.get('clinicalSkills')),
            communication: parseInt(formData.get('communication')),
            professionalism: parseInt(formData.get('professionalism')),
            teamwork: parseInt(formData.get('teamwork')),
            initiative: parseInt(formData.get('initiative'))
          })
        }
      );
      
      if (response.ok) {
        alert('✅ Performance evaluation submitted successfully!');
        fetchPostings();
        const posting = postings.find(p => p._id === selectedStudent._id);
        setSelectedStudent(posting);
        setActiveTab('overview');
      } else {
        alert('❌ Failed to submit evaluation');
      }
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      alert('❌ Error submitting evaluation');
    }
  };

  const handleCompletePosting = async (postingId) => {
    if (!window.confirm('Mark this DRP posting as completed? This will generate the completion certificate.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/drpc/posting/${postingId}/complete`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        alert('✅ DRP posting completed successfully! Certificate will be generated.');
        fetchPostings();
        setSelectedStudent(null);
      } else {
        const data = await response.json();
        alert('❌ ' + (data.message || 'Failed to complete posting'));
      }
    } catch (error) {
      console.error('Error completing posting:', error);
      alert('❌ Error completing posting');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  if (!user) {
    return null;
  }

  const drpcName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || 'DRPC';
  const district = user.drpcDetails?.district || 'District';

  return (
    <div className="drpc-dashboard-container_0082">
      {/* Header */}
      <header className="drpc-header_0082">
        <div className="header-left_0082">
          <div className="logo-container_0082">
            <img 
              src={collegeConfig.logo} 
              alt={`${collegeConfig.shortName} Logo`} 
              className="drpc-logo_0082" 
            />
            <div className="logo-text_0082">
              <h2>{collegeConfig.shortName}</h2>
              <p>DRPC Dashboard</p>
            </div>
          </div>
        </div>

        <div className="header-right_0082">
          <div className="profile-section_0082" onClick={() => setShowProfileMenu(!showProfileMenu)}>
            <div className="profile-avatar_0082">
              <FaUserMd />
            </div>
            <div className="profile-info_0082">
              <span className="profile-name_0082">{drpcName}</span>
              <span className="profile-role_0082">DRPC - {district}</span>
            </div>
            <span className="dropdown-arrow_0082">▼</span>
            
            {showProfileMenu && (
              <div className="profile-dropdown_0082">
                <div className="dropdown-item_0082 logout_0082" onClick={handleLogout}>
                  <span className="dropdown-icon_0082"><FaSignOutAlt /></span>
                  Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Welcome Section */}
      <div className="welcome-section_0082">
        <h1>Welcome, {drpcName}!</h1>
        <p>
          <FaMapMarkerAlt /> {user.drpcDetails?.hospitalName || 'District Hospital'}, {district}, {user.drpcDetails?.state || ''}
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid_0082">
        <div className="stat-card_0082" style={{ '--card-color': '#3498DB' }}>
          <div className="stat-icon_0082">
            <FaUserGraduate />
          </div>
          <div className="stat-content_0082">
            <h3>{stats.totalStudents}</h3>
            <p>Total Students</p>
          </div>
        </div>

        <div className="stat-card_0082" style={{ '--card-color': '#F39C12' }}>
          <div className="stat-icon_0082">
            <FaClock />
          </div>
          <div className="stat-content_0082">
            <h3>{stats.pendingAttendance}</h3>
            <p>Pending Attendance</p>
          </div>
        </div>

        <div className="stat-card_0082" style={{ '--card-color': '#E74C3C' }}>
          <div className="stat-icon_0082">
            <FaFileAlt />
          </div>
          <div className="stat-content_0082">
            <h3>{stats.pendingReports}</h3>
            <p>Pending Reports</p>
          </div>
        </div>

        <div className="stat-card_0082" style={{ '--card-color': '#2ECC71' }}>
          <div className="stat-icon_0082">
            <FaCheckCircle />
          </div>
          <div className="stat-content_0082">
            <h3>{stats.completedPostings}</h3>
            <p>Completed</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="loading-state_0082">
          <div className="spinner_0082"></div>
          <p>Loading students...</p>
        </div>
      ) : error ? (
        <div className="error-state_0082">{error}</div>
      ) : !selectedStudent ? (
        // Students List View
        <div className="students-section_0082">
          <h2>Students in Your District</h2>
          
          {postings.length === 0 ? (
            <div className="empty-state_0082">
              <FaHospital size={60} color="#ccc" />
              <h3>No Students Assigned</h3>
              <p>No PG students have been posted to {district} yet.</p>
            </div>
          ) : (
            <div className="students-grid_0082">
              {postings.map((posting) => (
                <div key={posting._id} className="student-card_0082">
                  <div className="student-header_0082">
                    <div className="student-avatar_0082">
                      {posting.student?.profile?.firstName?.charAt(0)}
                      {posting.student?.profile?.lastName?.charAt(0)}
                    </div>
                    <div className="student-info_0082">
                      <h3>
                        {posting.student?.profile?.firstName} {posting.student?.profile?.lastName}
                      </h3>
                      <p>{posting.student?.profile?.registrationNumber}</p>
                      <span className="department-badge_0082">{posting.postingDetails?.department}</span>
                    </div>
                  </div>

                  <div className="student-details_0082">
                    <div className="detail-row_0082">
                      <FaCalendarAlt />
                      <span>
                        {new Date(posting.postingDetails?.startDate).toLocaleDateString()} - 
                        {new Date(posting.postingDetails?.endDate).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="detail-row_0082">
                      <FaChartBar />
                      <span>Attendance: {posting.attendanceStats?.attendancePercentage?.toFixed(1) || 0}%</span>
                    </div>

                    <div className="detail-row_0082">
                      <FaClock />
                      <span>
                        {posting.attendance?.filter(a => !a.verifiedByDRPC).length || 0} pending verification
                      </span>
                    </div>

                    <div className="detail-row_0082">
                      <FaFileAlt />
                      <span>
                        {posting.weeklyReports?.filter(r => !r.verifiedByDRPC).length || 0} reports pending
                      </span>
                    </div>
                  </div>

                  <div className="student-actions_0082">
                    <button 
                      className="btn-primary_0082"
                      onClick={() => {
                        setSelectedStudent(posting);
                        setActiveTab('overview');
                      }}
                    >
                      <FaEye /> View Details
                    </button>
                  </div>

                  {posting.isCompleted && (
                    <div className="completed-badge_0082">
                      <FaCheckCircle /> Completed
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Student Detail View
        <div className="student-detail-view_0082">
          <div className="detail-header_0082">
            <button className="back-button_0082" onClick={() => setSelectedStudent(null)}>
              ← Back to Students
            </button>
            <h2>
              {selectedStudent.student?.profile?.firstName} {selectedStudent.student?.profile?.lastName}
            </h2>
          </div>

          {/* Tabs */}
          <div className="detail-tabs_0082">
            <button 
              className={activeTab === 'overview' ? 'active_0082' : ''}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={activeTab === 'attendance' ? 'active_0082' : ''}
              onClick={() => setActiveTab('attendance')}
            >
              Attendance ({selectedStudent.attendance?.filter(a => !a.verifiedByDRPC).length || 0})
            </button>
            <button 
              className={activeTab === 'reports' ? 'active_0082' : ''}
              onClick={() => setActiveTab('reports')}
            >
              Weekly Reports ({selectedStudent.weeklyReports?.filter(r => !r.verifiedByDRPC).length || 0})
            </button>
            <button 
              className={activeTab === 'evaluate' ? 'active_0082' : ''}
              onClick={() => setActiveTab('evaluate')}
            >
              Evaluate
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content_0082">
            {activeTab === 'overview' && (
              <div className="overview-tab_0082">
                <div className="info-grid_0082">
                  <div className="info-card_0082">
                    <h4>Student Information</h4>
                    <p><strong>Name:</strong> {selectedStudent.student?.profile?.firstName} {selectedStudent.student?.profile?.lastName}</p>
                    <p><strong>Registration:</strong> {selectedStudent.student?.profile?.registrationNumber}</p>
                    <p><strong>Email:</strong> {selectedStudent.student?.email}</p>
                    <p><strong>Mobile:</strong> {selectedStudent.student?.profile?.mobile}</p>
                  </div>

                  <div className="info-card_0082">
                    <h4>Posting Details</h4>
                    <p><strong>Department:</strong> {selectedStudent.postingDetails?.department}</p>
                    <p><strong>Semester:</strong> {selectedStudent.postingDetails?.semester}</p>
                    <p><strong>Duration:</strong> {selectedStudent.postingDetails?.totalDays} days</p>
                    <p><strong>Status:</strong> {selectedStudent.isCompleted ? 'Completed' : 'Active'}</p>
                  </div>

                  <div className="info-card_0082">
                    <h4>Progress Statistics</h4>
                    <p><strong>Attendance:</strong> {selectedStudent.attendanceStats?.attendancePercentage?.toFixed(1)}%</p>
                    <p><strong>Present:</strong> {selectedStudent.attendanceStats?.presentDays}/{selectedStudent.attendanceStats?.totalDays} days</p>
                    <p><strong>Activities Logged:</strong> {selectedStudent.dailyActivities?.length || 0}</p>
                    <p><strong>Reports Submitted:</strong> {selectedStudent.weeklyReports?.length || 0}</p>
                  </div>

                  {selectedStudent.performance?.drpcScore && (
                    <div className="info-card_0082">
                      <h4>Performance</h4>
                      <p><strong>DRPC Score:</strong> {selectedStudent.performance.drpcScore}/100</p>
                      {selectedStudent.performance.finalScore && (
                        <>
                          <p><strong>Final Score:</strong> {selectedStudent.performance.finalScore}/100</p>
                          <p><strong>Grade:</strong> {selectedStudent.performance.grade}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {!selectedStudent.isCompleted && selectedStudent.attendanceStats?.attendancePercentage >= 90 && selectedStudent.performance?.drpcScore && (
                  <div className="completion-section_0082">
                    <button 
                      className="btn-success_0082"
                      onClick={() => handleCompletePosting(selectedStudent._id)}
                    >
                      <FaAward /> Mark as Completed & Generate Certificate
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'attendance' && (
              <div className="attendance-tab_0082">
                <div className="tab-header_0082">
                  <h3>Attendance Records</h3>
                  {selectedStudent.attendance?.some(a => !a.verifiedByDRPC) && (
                    <button 
                      className="btn-secondary_0082"
                      onClick={() => {
                        const pendingIds = selectedStudent.attendance
                          .filter(a => !a.verifiedByDRPC)
                          .map(a => a._id);
                        handleBulkVerifyAttendance(selectedStudent._id, pendingIds);
                      }}
                    >
                      <FaCheck /> Verify All Pending
                    </button>
                  )}
                </div>

                {selectedStudent.attendance && selectedStudent.attendance.length > 0 ? (
                  <div className="attendance-list_0082">
                    {selectedStudent.attendance
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((att) => (
                      <div key={att._id} className={`attendance-item_0082 ${att.verifiedByDRPC ? 'verified_0082' : 'pending_0082'}`}>
                        <div className="att-date_0082">
                          <FaCalendarAlt />
                          {new Date(att.date).toLocaleDateString()}
                        </div>
                        <div className="att-time_0082">
                          <FaClock /> {att.checkInTime}
                        </div>
                        <div className="att-status_0082">
                          {att.verifiedByDRPC ? (
                            <span className="verified-badge_0082">
                              <FaCheckCircle /> Verified
                            </span>
                          ) : (
                            <button 
                              className="btn-verify_0082"
                              onClick={() => handleVerifyAttendance(selectedStudent._id, att._id)}
                            >
                              <FaCheck /> Verify
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data_0082">No attendance records yet</p>
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="reports-tab_0082">
                <h3>Weekly Reports</h3>
                {selectedStudent.weeklyReports && selectedStudent.weeklyReports.length > 0 ? (
                  <div className="reports-list_0082">
                    {selectedStudent.weeklyReports
                      .sort((a, b) => b.weekNumber - a.weekNumber)
                      .map((report) => (
                      <div key={report._id} className="report-item_0082">
                        <div className="report-header_0082">
                          <h4>Week {report.weekNumber}</h4>
                          <span className="report-date_0082">
                            {new Date(report.weekStartDate).toLocaleDateString()} - 
                            {new Date(report.weekEndDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="report-content_0082">
                          <p><strong>Summary:</strong> {report.summary}</p>
                          <p><strong>Key Learnings:</strong> {report.keyLearnings}</p>
                          <p><strong>Challenges:</strong> {report.challenges}</p>
                          <p><strong>Cases Handled:</strong> {report.casesHandled}</p>
                        </div>
                        {report.verifiedByDRPC ? (
                          <div className="verified-section_0082">
                            <FaCheckCircle /> Verified
                            {report.drpcFeedback && (
                              <p><strong>Your Feedback:</strong> {report.drpcFeedback}</p>
                            )}
                          </div>
                        ) : (
                          <div className="verify-section_0082">
                            <textarea 
                              placeholder="Add your feedback (optional)"
                              id={`feedback-${report._id}`}
                              rows="3"
                            />
                            <button 
                              className="btn-primary_0082"
                              onClick={() => {
                                const feedback = document.getElementById(`feedback-${report._id}`).value;
                                handleVerifyReport(selectedStudent._id, report._id, feedback);
                              }}
                            >
                              <FaCheck /> Verify Report
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data_0082">No weekly reports submitted yet</p>
                )}
              </div>
            )}

            {activeTab === 'evaluate' && (
              <div className="evaluate-tab_0082">
                <h3>Performance Evaluation</h3>
                
                {selectedStudent.performance?.drpcScore ? (
                  <div className="evaluation-complete_0082">
                    <FaCheckCircle size={50} color="#2ECC71" />
                    <h3>Evaluation Already Submitted</h3>
                    <p><strong>Score:</strong> {selectedStudent.performance.drpcScore}/100</p>
                    <p><strong>Remarks:</strong> {selectedStudent.performance.drpcRemarks}</p>
                  </div>
                ) : (
                  <form onSubmit={handleEvaluate} className="evaluation-form_0082">
                    <div className="form-section_0082">
                      <h4>Detailed Assessment (0-10 each)</h4>
                      
                      <div className="form-row_0082">
                        <div className="form-group_0082">
                          <label>Clinical Skills *</label>
                          <input type="number" name="clinicalSkills" min="0" max="10" required />
                        </div>
                        
                        <div className="form-group_0082">
                          <label>Communication *</label>
                          <input type="number" name="communication" min="0" max="10" required />
                        </div>
                      </div>

                      <div className="form-row_0082">
                        <div className="form-group_0082">
                          <label>Professionalism *</label>
                          <input type="number" name="professionalism" min="0" max="10" required />
                        </div>
                        
                        <div className="form-group_0082">
                          <label>Teamwork *</label>
                          <input type="number" name="teamwork" min="0" max="10" required />
                        </div>
                      </div>

                      <div className="form-group_0082">
                        <label>Initiative *</label>
                        <input type="number" name="initiative" min="0" max="10" required />
                      </div>
                    </div>

                    <div className="form-section_0082">
                      <h4>Overall Evaluation</h4>
                      
                      <div className="form-group_0082">
                        <label>DRPC Score (0-100) *</label>
                        <input type="number" name="drpcScore" min="0" max="100" required />
                      </div>

                      <div className="form-group_0082">
                        <label>Detailed Remarks *</label>
                        <textarea 
                          name="drpcRemarks" 
                          rows="5" 
                          placeholder="Write detailed evaluation remarks..."
                          required
                        />
                      </div>
                    </div>

                    <div className="form-actions_0082">
                      <button type="submit" className="btn-primary_0082">
                        <FaAward /> Submit Evaluation
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DRPCDashboard;